import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  selectAuthLoading,
  selectCurrentFreeUser
} from "../../features/auth/authSelectors";
import {
  TextField,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Avatar,
  Box,
  styled,
  Alert,
  useMediaQuery,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormGroup,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import { ReactComponent as CameraIcon } from "../../assets/camera.svg";
import { ReactComponent as EditIcon } from "../../assets/edit.svg";
import { getFreeUserData, registerUser } from "../../features/auth/authSlice";
import {
  CheckBox,
  CheckBoxOutlineBlank,
  Visibility,
  VisibilityOff
} from "@mui/icons-material";
import { uploadFile } from "../../utils/uploadFile";
import { getUniqueSuffix } from "../../utils/getUniqueSuffix";
import useSignedUrl from "../../utils/useSignedUrl";
import CountrySelect from "../Extra/CountryPicker";

const StyledTextFieldContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  margin: theme.spacing(4, 0)
}));

const StyledInputLabel = styled("label")(({ theme }) => ({
  position: "absolute",
  top: "-18px",
  color: "#14142B",
  fontWeight: "bold",
  fontSize: "14px",
  pointerEvents: "none"
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  input: {
    ":disabled": {
      backgroundColor: "#A7C8E9",
      color: "#FCFCFC",
      borderRadius: "4px"
    }
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      color: "#9CA3AF",
      borderColor: "#D1D5DB"
    },
    "&:hover fieldset": {
      borderColor: "#1C1C1C"
    },
    "&.Mui-focused fieldset": {
      color: "#1C1C1C",
      borderColor: "#D1D5DB"
    }
  }
}));

const returnOptions = (role) => {
  const companyOptions = [
    // {
    //   label: "$3000 / year up to 50 users",
    //   value: "3000",
    //   text: ["3000", "/ 50 users/yr"],
    //   seats: 50
    // },
    // {
    //   label: "$6000 / year up to 100 users",
    //   value: "6000",
    //   text: ["6000", "/ 100 users/yr"],
    //   seats: 100
    // },
    {
      label: "$5,000 / year up to 250 users",
      value: "5000",
      text: ["5000", "/ 250 users/yr"],
      seats: 250
    },
    // {
    //   label: "$30,000 / year up to 500 users",
    //   value: "30000",
    //   text: ["30000", "/ 500 users/yr"],
    //   seats: 500
    // }
  ];

  const gymOptions = [
    {
      label: "$397 / year up to 250 users",
      value: "397",
      text: ["397", "/ 250 seat/yr"],
      seats: 250
    },
    // {
    //   label: "$500 / year up to 50 users",
    //   value: "500",
    //   text: ["500", "/ 50 seat/yr"],
    //   seats: 50
    // },
    // {
    //   label: "$1,000  / year up to 100 users",
    //   value: "1000",
    //   text: ["1000", "/ 100 seat/yr"],
    //   seats: 100
    // },
    // {
    //   label: "$2,500 / year up to 250 users",
    //   value: "2500",
    //   text: ["2500", "/ 250 seat/yr"],
    //   seats: 250
    // },
    // {
    //   label: "$5,000 / year up to 500 users",
    //   value: "5000",
    //   text: ["5000", "/ 500 seat/yr"],
    //   seats: 500
    // }
  ];

  const coachOptions = [
    {
      label: "$250 / year up to 25 users",
      value: "250",
      text: ["250", "/ 25 seat/yr"],
      seats: 25
    },
    {
      label: "$500 / year up to 50 users",
      value: "500",
      text: ["500", "/ 50 seat/yr"],
      seats: 50
    },
    {
      label: "$1,250 / year up to 125 users",
      value: "1250",
      text: ["1250", "/ 125 seat/yr"],
      seats: 125
    },
    {
      label: "$2,500 / year up to 250 users",
      value: "2500",
      text: ["2500", "/ 250 seat/yr"],
      seats: 250
    }
  ];

  switch (role) {
    case "Gym":
      return gymOptions;
    case "Company":
      return companyOptions;
    case "Coach":
      return coachOptions;
    default:
      return gymOptions;
  }
};

const Register = () => {
  const location = useLocation();
  const { role: mainRole } = location.state || { role: null };
  const [role, setRole] = useState("Gym");
  const { inActiveUser } = location.state || { inActiveUser: null };
  useEffect(() => {
    if (inActiveUser?.userType) setRole(inActiveUser?.userType);
    else if (mainRole) setRole(mainRole);
  }, [mainRole, inActiveUser]);

  useEffect(() => {
    setSelectedPlan(returnOptions(role)?.[0]?.value);
  }, [role]);
  const isCoach = role === "Coach" || role === "Affiliate";
  const isGym = role === "Gym";
  // const isCompany = role === "Company";
  const [formData, setFormData] = useState({
    fullName: "",
    displayName: "",
    gymName: "",
    companyName: "",
    email: "",
    password: "",
    profilePic: null,
    bio: "",
    url: "",
    code: "",
    country: "US",
    capabilities: ["transfers", "card_payments"],
    isSuperAdmin: false,
    termsconditions: false
  });
  const [selectedPlan, setSelectedPlan] = useState(
    returnOptions(role)?.[0]?.value
  );

  const [formErrors, setFormErrors] = useState({
    fullName: false,
    displayName: false,
    email: false,
    password: false,
    bio: false,
    url: false
  });
  const matches = useMediaQuery("(max-width:590px)");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  useEffect(() => {
    if (inActiveUser)
      setFormData({
        fullName: inActiveUser?.fullName,
        displayName: inActiveUser?.displayName,
        gymName: inActiveUser?.referenceData?.gymName,
        companyName: inActiveUser?.referenceData?.companyName,
        country: inActiveUser?.referenceData?.country,
        email: inActiveUser?.email,
        password: "TestingPassword123!",
        profilePic: null,
        bio: inActiveUser?.bio,
        url: inActiveUser?.url,
        ...(inActiveUser?.userType === "Coach" && { code: "" })
      });
  }, [inActiveUser]);
  const imgSrc = useSignedUrl(
    inActiveUser?.profilePic,
    process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
  );
  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };
  const dispatch = useDispatch();
  const loading = useSelector(selectAuthLoading);
  const navigate = useNavigate();

  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);
  const validateUrl = (url) =>
    /(([\w]+:)?\/\/)?(([\d\w]|%[a-fA-f\d]{2,2})+(:([\d\w]|%[a-fA-f\d]{2,2})+)?@)?([\d\w][-\d\w]{0,253}[\d\w]\.)+[\w]{2,63}(:[\d]+)?(\/([-+_~.\d\w]|%[a-fA-f\d]{2,2})*)*(\?(&?([-+_~.\d\w]|%[a-fA-f\d]{2,2})=?)*)?(#([-+_~.\d\w]|%[a-fA-f\d]{2,2})*)?/.test(
      url
    );
  const validatePassword = (password) =>
    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-])[A-Za-z\d#?!@$%^&*-]{8,}$/.test(
      password
    );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]:
        type === "checkbox"
          ? checked
          : name === "bio"
          ? value.length < 51
            ? value
            : formData?.bio
          : value
    });

    if (name === "email") {
      setFormErrors({ ...formErrors, email: !validateEmail(value) });
    } else if (name === "url") {
      setFormErrors({ ...formErrors, url: !validateUrl(value) });
    } else if (name === "password") {
      setFormErrors({ ...formErrors, password: !validatePassword(value) });
    }
    // else if (name === "bio" && value.length && value.length > 50) {
    //   setFormErrors({ ...formErrors, bio: true });
    // }
    else {
      setFormErrors({ ...formErrors, [name]: value.trim() === "" });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 30 * 1024 * 1024) {
      setSnackbarMessage("File size should be less than 30MB.");
      setOpenSnackbar(true);
      return;
    }
    if (file) {
      setFormData((prevState) => ({
        ...prevState,
        profilePic: file
      }));
    }
  };
  useEffect(() => {
    dispatch(getFreeUserData());
  }, [dispatch]);
  const isFreeUserAllowed = useSelector(selectCurrentFreeUser);
  const handleRegister = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) return;

    // Set submission flag
    setIsSubmitting(true);
    try {
      if (inActiveUser) {
        const { payload } = await dispatch(
          registerUser({
            mainData: {
              userId: inActiveUser?.userId,
              pendingVerification: true,
              selectedPlan
            },
            role: role.toLowerCase()
          })
        );
        if (payload?.sessionUrl) {
          window.location.href = payload?.sessionUrl;
        } else {
          setSnackbarMessage(payload.error || "Registration failed.");
          setOpenSnackbar(true);
        }
        return;
      }
      if (Object.values(formErrors).some((error) => error)) {
        setSnackbarMessage("Please correct the form errors.");
        setOpenSnackbar(true);
        return;
      }
      if (!formData.termsconditions) {
        setSnackbarMessage("Please accept the Terms of Service & Privacy Policy.");
        setOpenSnackbar(true);
        return;
      }
      if (isCoach) {
        delete formData.gymName;
        delete formData.companyName;
        if (!formData.code) {
          setSnackbarMessage("Please provide correct code before proceeding");
          setOpenSnackbar(true);
          return;
        }
      }
      if (isGym) {
        delete formData.companyName;
        if (!formData.code) {
          setSnackbarMessage("Please provide correct Gym code before proceeding");
          setOpenSnackbar(true);
          return;
        }
      }
      delete formData.termsconditions;
      const uniqueFileName = getUniqueSuffix(formData.profilePic?.name);
      const { payload } = await dispatch(
        registerUser({
          mainData: {
            ...formData,
            userType: role,
            ...(formData.profilePic && {
              profilePic: uniqueFileName
            }),
            ...(!isCoach
              ? {
                  [`${role?.toLowerCase()}Name`]:
                    formData[`${role?.toLowerCase()}Name`]
                }
              : {}),
            selectedPlan,
            maxSeats: currentPlanDetails?.seats
          },
          role: role.toLowerCase()
        })
      );
      if (formData.profilePic)
        await uploadFile(
          formData.profilePic,
          uniqueFileName,
          process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
        );
      if (payload?.sessionUrl) {
        window.location.href = payload?.sessionUrl;
      } else if (payload?.message) {
        setSnackbarMessage(payload.message || "Registration successfully.");
        setOpenSnackbar(true);
        if (payload?.token) {
          localStorage.setItem("adminToken", payload?.token);
          localStorage.setItem("admin", JSON.stringify(payload?.user));
        }
        setTimeout(
          () => navigate("/", { state: { openConnectSetup: true } }),
          2000
        );
      } else {
        setSnackbarMessage(payload.error || "Registration failed.");
        setOpenSnackbar(true);
      }
    } catch (err) {
      setSnackbarMessage("Registration failed.");
      setOpenSnackbar(true);
    } finally {
      // Reset after some delay to prevent rapid clicks
      setTimeout(() => {
        setIsSubmitting(false);
      }, 2000);
    }
  };
  const fileInputRef = useRef();
  const handleEditClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const currentPlanDetails = useMemo(() => {
    return returnOptions(role)?.find((item) => item.value === selectedPlan);
  }, [selectedPlan, role]);

  return (
    <Box
      display={"flex"}
      width="100%"
      overflow="hidden"
      height="100vh"
      sx={{
        flexDirection: { xs: "column", md: "row" }
      }}
    >
      <Box
        sx={{
          left: 0,
          top: 0,
          width: { xs: 0, md: "50%" },
          display: { xs: "none", md: "block" },
          objectFit: "cover"
        }}
      >
        <Box
          component="img"
          src="/images/base.jpg"
          alt="base"
          sx={{
            width: "100%",
            height: "100vh",
            objectFit: "cover"
          }}
        />
      </Box>
      <Box
        sx={{
          left: 0,
          top: 0,
          width: { md: "0" },
          objectFit: "cover",
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          gap: "20px",
          alignItems: "center",
          marginTop: "20px"
        }}
      >
        <Box
          component="img"
          src="/images/threepillars.png"
          alt="base"
          sx={{
            width: "100px",
            height: "100px",
            objectFit: "cover"
          }}
        />
      </Box>
      <Box
        flexGrow={1}
        paddingY={3}
        paddingLeft={10}
        sx={{
          paddingTop: { xs: 5, md: 8, lg: 10 },
          paddingRight: { xs: 5, md: 13, lg: 20 },
          paddingBottom: { xs: 5, md: 8, lg: 10 },
          paddingLeft: { xs: 5, md: 10 },
          height: { xs: "93%", md: "80%" },
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(255, 255, 255, 0.5)",
          "&::-webkit-scrollbar": {
            width: "4px"
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            borderRadius: "6px"
          }
        }}
      >
        <Typography
          sx={{
            ...(matches && { display: "flex", justifyContent: "center" })
          }}
          fontSize="30px"
          fontWeight="bold"
          gutterBottom
        >
          Create account for {role?.toLowerCase()}
        </Typography>
        <Snackbar
          open={openSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          autoHideDuration={1000}
          onClose={() => setOpenSnackbar(false)}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={
              snackbarMessage?.toLowerCase()?.includes("success")
                ? "success"
                : "error"
            }
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
        <Box component={"form"} mt={2} onSubmit={handleRegister}>
          <input
            ref={fileInputRef}
            accept="image/*"
            type="file"
            onChange={handleFileChange}
            style={{ display: "none" }}
            id="profile-pic-upload"
          />
          <Box
            sx={{
              marginBottom: 6,
              ...(matches && { justifyContent: "center", alignItems: "center" })
            }}
            display="flex"
            flexDirection="column"
          >
            <Typography
              color="#14142B"
              fontWeight="bold"
              fontSize="14px"
              pointerEvents="none"
              htmlFor="avatar"
            >
              {role} Logo
            </Typography>
            {inActiveUser ? (
              imgSrc ? (
                <Avatar
                  alt={inActiveUser?.fullName}
                  src={imgSrc}
                  sx={{
                    width: 76,
                    height: 76,
                    marginTop: 2
                  }}
                />
              ) : (
                <Avatar
                  src={"null"}
                  alt={inActiveUser?.fullName}
                  sx={{
                    width: 76,
                    height: 76,
                    marginTop: 2
                  }}
                />
              )
            ) : formData.profilePic ? (
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <label htmlFor="profile-pic-upload">
                  <Avatar
                    alt="Profile Pic"
                    src={URL.createObjectURL(formData.profilePic)}
                    sx={{
                      width: 76,
                      height: 76,
                      marginTop: 2,
                      border: "1px solid #1976d2"
                    }}
                  />
                  <IconButton
                    onClick={handleEditClick}
                    sx={{
                      position: "absolute",
                      bottom: 5, // Adjust this value to move the icon slightly outside the avatar if desired
                      left: 55, // Adjust this value to move the icon slightly outside the avatar if desired
                      backgroundColor: "#1976d2",
                      borderRadius: "50%",
                      height: 15,
                      width: 15,
                      border: "1px solid #1976d2",
                      fontSize: 24, // Adjust the size as needed
                      padding: "2px",
                      svg: {
                        path: {
                          fill: "#fff"
                        }
                      }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </label>
              </Box>
            ) : (
              <label htmlFor="profile-pic-upload">
                <Button
                  variant="contained"
                  component="span"
                  color="default"
                  style={{
                    borderRadius: "48%",
                    backgroundColor: "#ccc",
                    width: 76,
                    height: 76,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: 16,
                    cursor: "pointer"
                  }}
                >
                  <CameraIcon style={{ width: 24, height: 24 }} />
                </Button>
              </label>
            )}
          </Box>
          {isCoach ? (
            <StyledTextFieldContainer>
              <StyledInputLabel htmlFor="fullName">Name</StyledInputLabel>
              <StyledTextField
                fullWidth
                variant="outlined"
                name="fullName"
                placeholder="Enter name"
                value={formData.fullName}
                disabled={inActiveUser}
                onChange={handleInputChange}
                required
                error={formErrors.fullName}
                helperText={formErrors.fullName && "Name is required"}
                margin="normal"
              />
            </StyledTextFieldContainer>
          ) : (
            <StyledTextFieldContainer>
              <StyledInputLabel htmlFor="fullName">Admin name</StyledInputLabel>
              <StyledTextField
                fullWidth
                variant="outlined"
                name="fullName"
                placeholder="Enter admin name"
                disabled={inActiveUser}
                value={formData.fullName}
                onChange={handleInputChange}
                required
                error={formErrors.fullName}
                helperText={formErrors.fullName && "Admin name is required"}
                margin="normal"
              />
            </StyledTextFieldContainer>
          )}
          {isCoach && (
            <>
              <StyledTextFieldContainer>
                <StyledInputLabel htmlFor="displayName">
                  Username (Displayed in app)
                </StyledInputLabel>
                <StyledTextField
                  fullWidth
                  disabled={inActiveUser}
                  variant="outlined"
                  name="displayName"
                  placeholder="Enter username"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  required
                  error={formErrors.displayName}
                  helperText={formErrors.displayName && "Username is required"}
                  margin="normal"
                />
              </StyledTextFieldContainer>
              <StyledTextFieldContainer>
                <StyledInputLabel htmlFor="bio">
                  Short Bio (50 character max)
                </StyledInputLabel>
                <StyledTextField
                  sx={{
                    "& .MuiInputBase-root": {
                      color: "#1C1C1C"
                    }
                  }}
                  fullWidth
                  variant="outlined"
                  name="bio"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Enter bio"
                  disabled={inActiveUser}
                  value={formData.bio}
                  multiline
                  rows={4}
                  onChange={handleInputChange}
                  margin="normal"
                  error={formData.bio && formErrors.bio}
                  helperText={
                    formData.bio &&
                    formErrors.bio &&
                    "Bio should be less than 50 characters"
                  }
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: formData.bio.length > 50 ? "red" : "#D1D5DB",
                    position: "absolute",
                    fontSize: "15px",
                    right: 10,
                    bottom: 10 // Adjust this value as necessary
                  }}
                >
                  {formData.bio.length}/50
                </Typography>
              </StyledTextFieldContainer>
            </>
          )}
          {!isCoach && (
            <StyledTextFieldContainer>
              <StyledInputLabel htmlFor={`${role?.toLowerCase()}Name`}>
                {role} Name
              </StyledInputLabel>
              <StyledTextField
                fullWidth
                disabled={inActiveUser}
                variant="outlined"
                name={`${role?.toLowerCase()}Name`}
                htmlFor={`${role?.toLowerCase()}Name`}
                placeholder={`Enter ${role?.toLowerCase()} name`}
                value={formData[`${role?.toLowerCase()}Name`]}
                onChange={handleInputChange}
                required
                error={formErrors[`${role?.toLowerCase()}Name`]}
                helperText={
                  formErrors[`${role?.toLowerCase()}Name`] &&
                  `${role} name is required`
                }
                margin="normal"
              />
            </StyledTextFieldContainer>
          )}
          <StyledTextFieldContainer>
            <StyledInputLabel htmlFor="email">
              {!isCoach ? "Admin email" : "Email"} address
            </StyledInputLabel>
            <StyledTextField
              fullWidth
              variant="outlined"
              name="email"
              type="email"
              placeholder="Enter email address"
              value={formData.email}
              disabled={inActiveUser}
              onChange={handleInputChange}
              required
              error={formErrors.email}
              helperText={formErrors.email && "Enter a valid email address"}
              margin="normal"
            />
          </StyledTextFieldContainer>
          <StyledTextFieldContainer>
            <StyledInputLabel htmlFor="password">Password</StyledInputLabel>
            <StyledTextField
              fullWidth
              disabled={inActiveUser}
              variant="outlined"
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      disabled={inActiveUser}
                      onMouseDown={handleMouseDownPassword}
                      onMouseUp={handleMouseUpPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              name="password"
              placeholder="Enter Password"
              value={formData.password}
              onChange={handleInputChange}
              required
              error={formErrors.password}
              helperText={
                formErrors.password &&
                "Password must be 8 characters long: Contains 1 upper, 1 lower case, 1 symbol and 1 digit"
              }
              margin="normal"
            />
          </StyledTextFieldContainer>
          {/* {isCoach && ( */}
          <StyledTextFieldContainer>
            <StyledInputLabel htmlFor="url">
              Profile link (website, social media, etc.)
            </StyledInputLabel>
            <StyledTextField
              fullWidth
              variant="outlined"
              name="url"
              placeholder="Enter URL"
              disabled={inActiveUser}
              value={formData.url}
              onChange={handleInputChange}
              error={formErrors.url}
              helperText={formErrors.url && "Enter a valid URL"}
              margin="normal"
            />
          </StyledTextFieldContainer>
          {inActiveUser ? (
            <></>
          ) : (
            <CountrySelect
              setCapabilityValue={(e) =>
                setFormData((prev) => ({ ...prev, capabilities: e }))
              }
              setValue={(e) => setFormData((prev) => ({ ...prev, country: e }))}
            />
          )}
          {isCoach && (
            <StyledTextFieldContainer>
              <StyledInputLabel htmlFor="code">
                {role === "Coach" ? "Coaches" : role} Code
              </StyledInputLabel>
              <StyledTextField
                fullWidth
                variant="outlined"
                disabled={inActiveUser}
                name="code"
                placeholder="Enter Code"
                value={formData.code}
                onChange={handleInputChange}
                margin="normal"
              />
            </StyledTextFieldContainer>
          )}
          {isGym && (
            <StyledTextFieldContainer>
              <StyledInputLabel htmlFor="code">Gym Code</StyledInputLabel>
              <StyledTextField
                fullWidth
                variant="outlined"
                disabled={inActiveUser}
                name="code"
                placeholder="Enter Code"
                value={formData.code}
                onChange={handleInputChange}
                margin="normal"
              />
            </StyledTextFieldContainer>
          )}
          {/* )} */}
          {role === "Affiliate" ? (
            <></>
          ) : (
            <>
              <FormControl
                fullWidth
                variant="outlined"
                style={{
                  marginTop: -10,
                  marginBottom: 30,
                  borderColor: "#D1D5DB"
                }}
              >
                <InputLabel id="source-of-joining-label">
                  Pricing variants
                </InputLabel>
                <Select
                  labelId="source-of-joining-label"
                  id="source-of-joining"
                  name="userType"
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  label="Pricing variants"
                  sx={{
                    ".MuiOutlinedInput-notchedOutline": {
                      // borderColor: "#333" // Border color
                      borderColor: "#D1D5DB"
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#FCFCFC" // Border color when focused
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#FCFCFC" // Border color on hover
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        color: "#1C1C1C", // White font color for the menu items
                        "& .MuiMenuItem-root": {
                          padding: "10px" // Adjust padding inside menu items
                        }
                      }
                    }
                  }}
                >
                  {returnOptions(role).map((item) => (
                    <MenuItem value={item.value}>{item.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>{" "}
              <Box
                backgroundColor="#FCECD3"
                borderRadius="8px"
                border="1px solid #F1B44C"
                display="flex"
                padding="18px"
                alignItems="end"
                gap="3px"
                justifyContent="space-between"
              >
                <Typography fontWeight="bold">Plan</Typography>
                <Box display="flex" alignItems="end">
                  <Typography fontWeight="bold">
                    $ {currentPlanDetails?.text?.[0]}
                  </Typography>
                  <Typography fontSize="13px" color="#6B7280">
                    {currentPlanDetails?.text?.[1]}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
          {isFreeUserAllowed || role !== "Affiliate" ? (
            <></>
          ) : (
            <StyledTextFieldContainer sx={{ m: 1 }}>
              {" "}
              <FormGroup>
                {" "}
                <FormControlLabel
                  sx={{
                    color: "#4f4b4b",
                    fontWeight: "bold",
                    fontSize: "14px"
                  }}
                  control={
                    <Checkbox
                      name="isSuperAdmin"
                      checked={formData.isSuperAdmin}
                      onChange={() =>
                        setFormData({
                          ...formData,
                          isSuperAdmin: !formData.isSuperAdmin
                        })
                      }
                      icon={
                        <CheckBoxOutlineBlank
                          sx={{ color: "#4f4b4b" }} // Gray color when unchecked
                        />
                      }
                      checkedIcon={
                        <CheckBox
                          sx={{
                            color: "#1c1c1c",
                            borderRadius: "4px"
                          }}
                        />
                      }
                      sx={{
                        "&.Mui-checked": {
                          border: "none",
                          color: "#1976d2" // Green border when checked
                        }
                      }}
                    />
                  }
                  label="Allow users referred by you to access the platform for free?"
                />
              </FormGroup>
            </StyledTextFieldContainer>
          )}
          <FormControlLabel
            control={
              <Checkbox
                name="termsconditions"
                checked={formData.termsconditions}
                onChange={handleInputChange}
                icon={
                  <CheckBoxOutlineBlank
                    sx={{ color: "#D1D5DB" }}
                  />
                }
                checkedIcon={
                  <CheckBox
                    sx={{
                      color: "#000",
                      borderRadius: "4px"
                    }}
                  />
                }
                sx={{
                  "&.Mui-checked": {
                    border: "none",
                    color: "#368AD0"
                  }
                }}
              />
            }
            label={
              <>
                I accept the Three Pillars Method
                <a
                  href="https://optimizemenutrition.com/terms-of-use-for-online-programs-products/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none", color: "#2C75C5" }}
                >
                  {" "}
                  Terms of Service{" "}
                </a>
                &{" "}
                <a
                  href="https://optimizemenutrition.com/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none", color: "#2C75C5" }}
                >
                  {" "}
                  Privacy Policy{" "}
                </a>
              </>
            }
            sx={{
              color: "#000",
              mt: 2,
              fontWeight: "bold",
              fontSize: "14px",
              marginBottom: "16px",
              "& span": {
                fontSize: "15px"
              }
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            style={{
              marginTop: 25,
              textTransform: "none",
              backgroundColor: "#1976d2",
              color: "#fff",
              height: "42px",
              fontWeight: "bold"
            }}
            fullWidth
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : role === "Affiliate" ? (
              "Register"
            ) : (
              "Proceed to pay"
            )}
          </Button>
          <Box
            mt={3}
            display="flex"
            gap="20px"
            textAlign="center"
            justifyContent="center"
            alignItems="center"
          >
            <Link
              to="/partners"
              variant="outlined"
              style={{
                color: "#1976d2",
                textDecoration: "none"
              }}
            >
              Go Back
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Register;
